<template>
	<div class="map-tooltip" :style="coords" ref="tooltipRoot">
		<div :class="mapTooltipStyle">
			<div :class="headerClasses">
				<div class="flex-grow">{{ featureDataRef.mntpnt_int }}</div>
				<div v-if="!isCollapsed" class="collapse-icon">
					<DownCircleOutlined />
				</div>
			</div>
			<div v-if="!isCollapsed" class="map-tooltip-content">
				<div v-if="statsDisplay.length > 0" class="flex-wrapper">
					<GnssTag
						v-for="badge in statsDisplay"
						:key="badge.code"
						:code="badge.code"
						:forceGrey="badge.forceGrey"
						small
					/>
				</div>
				<div v-else class="col-chart-no-data">
					{{ $t('home-map.tooltip.labels.noData') }}
				</div>
			</div>
		</div>
	</div>
</template>

<script lang="ts">
import { toRef, computed, onMounted, ref, inject } from 'vue';
import type { PropType } from 'vue';
import { useEventListener } from '@vueuse/core';
import { DownCircleOutlined } from '@ant-design/icons-vue';

import GnssTag from '@/components/common/GnssTag.vue';

import { useLocaleData } from '@/locales/useLocaleData';

import { getStationGnssBadges } from '@/components/map/tooltips/vrsAccuracyMetricsUtils';
import type { GnssTooltipFeatureData } from '@/stores/types/mapStoreContracts';
import type { MetricDbItem } from '@/stores/types/mapStoreContracts';


type TooltipCoords = {
	left: string;
	top: string;
};

export default {
	components: {
		DownCircleOutlined, GnssTag,
	},
	props: {
		featureData: {
			type: Object as PropType<GnssTooltipFeatureData>,
			required: true,
		},
		coords: {
			type: Object as PropType<TooltipCoords>,
			required: true,
			validator: (value: TooltipCoords) => typeof value.left === 'string' && typeof value.top === 'string',
		},
		stats: {
			type: Object as PropType<MetricDbItem | null>,
			required: false,
			default: null,
		},
		hovered: {
			type: Boolean,
			required: true,
		},
		mapMode: {
			type: String,
			required: true,
		},
		focused: {
			type: Boolean,
			required: true,
		},
	},
	setup(props) {
		const isCollapsed = ref(false);
		const featureDataRef = toRef(props, 'featureData');
		const statsRef = toRef(props, 'stats');
		const hoveredRef = toRef(props, 'hovered');
		const focusedRef = toRef(props, 'focused');
		const statsDisplay = computed(() => getStationGnssBadges(statsRef.value));
		const { localeData } = useLocaleData();
		const mapTooltipStyle = computed(() => {
			let styles = [
				'map-tooltip-style',
				'map-tooltip-position-' + localeData.value.direction,
			];
			if (hoveredRef.value) {
				styles.push('map-tooltip-style-hovered');
			} else if (focusedRef.value) {
				styles.push('map-tooltip-style-focused');
			} else if (isCollapsed.value) {
				styles.push('map-tooltip-style-collapsed');
			}
			return styles;
		});

		const tooltipRoot = ref<HTMLElement | undefined>();
		const unhoverMapFeatures = inject<(() => void) | null>('unhoverMapFeatures', null);
		const eventListener = (updateCollapsed: boolean) => (event: Event): void => {
			// если !updateCollapsed событие все равно перехватываем, чтобы не было неожиданных взаимодействий с картой
			if (!(event.target instanceof Element) || event.target.closest('.map-tooltip-header') == null) {
				return;
			}
			if (updateCollapsed) {
				isCollapsed.value = !isCollapsed.value;
			}
			event.stopPropagation();
			if (event.type === 'pointermove' && unhoverMapFeatures != null) {
				unhoverMapFeatures();
			}
		};
		onMounted(() => {
			useEventListener(tooltipRoot.value, 'pointerdown', eventListener(false));
			useEventListener(tooltipRoot.value, 'pointermove', eventListener(false));
			useEventListener(tooltipRoot.value, 'click', eventListener(true));
		});
		const headerClasses = computed(() => ({
			'flex-wrapper': true,
			'gap-sm': true,
			'map-tooltip-header': true,
			'map-tooltip-header-collapsed': isCollapsed.value,
		}));
		return {
			statsDisplay,
			featureDataRef,
			mapTooltipStyle,
			tooltipRoot,
			isCollapsed,
			headerClasses,
		};
	},
}
</script>

<style scoped>
@import '@/assets/map-tooltip.css';

.col-chart-no-data {
	min-width: 30px;
	margin-top: 0;
	margin-bottom: 5px;
	color: rgba(0, 0, 0, 0.25);
	text-align: center;
}

.collapse-icon {
	opacity: 0.7;
}

.map-tooltip-header:hover .collapse-icon {
	opacity: 1;
}
</style>